import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { queryClient } from "../lib/queryClient";
import { getAlerts, acknowledgeAlert, resolveAlert, deleteAlert, getDevices, getDevice } from "../api/axios";
import { format } from "date-fns";
import { 
  ArrowLeft,
  AlertTriangle, 
  AlertOctagon, 
  AlertCircle, 
  Info, 
  CheckCircle, 
  Clock,
  RotateCw,
  Trash2,
  ExternalLink
} from "lucide-react";

export default function AlertDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch all alerts and find the one with matching ID
  const { data: alerts = [], isLoading: isLoadingAlerts } = useQuery({
    queryKey: ["/api/alerts"],
    queryFn: () => getAlerts({}),
  });

  // Fetch devices for device name lookup
  const { data: devices = [] } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
  });

  // Find the specific alert
  const alert = alerts.find((a: any) => (a._id || a.id)?.toString() === id?.toString());

  // Normalize deviceId - handle ObjectId string or object
  const normalizedDeviceId = alert?.deviceId 
    ? (typeof alert.deviceId === 'object' && alert.deviceId._id 
        ? alert.deviceId._id.toString() 
        : alert.deviceId.toString())
    : null;

  // Fetch device details if alert exists
  const { data: device } = useQuery({
    queryKey: ["device", normalizedDeviceId],
    queryFn: () => getDevice(normalizedDeviceId || ""),
    enabled: !!normalizedDeviceId,
  });

  // Acknowledge alert mutation
  const acknowledgeAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      return acknowledgeAlert(alertId);
    },
    onSuccess: () => {
      toast({
        title: "Alert acknowledged",
        description: "The alert has been acknowledged",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to acknowledge alert",
        variant: "destructive",
      });
    },
  });

  // Resolve alert mutation
  const resolveAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      return resolveAlert(alertId);
    },
    onSuccess: () => {
      toast({
        title: "Alert resolved",
        description: "The alert has been resolved",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
      navigate("/alerts");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to resolve alert",
        variant: "destructive",
      });
    },
  });

  // Delete alert mutation
  const deleteAlertMutation = useMutation({
    mutationFn: async (alertId: string) => {
      return deleteAlert(alertId);
    },
    onSuccess: () => {
      toast({
        title: "Alert deleted",
        description: "The alert has been permanently removed",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
      navigate("/alerts");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete alert",
        variant: "destructive",
      });
    },
  });

  if (isLoadingAlerts) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex justify-center items-center py-12">
          <RotateCw className="h-8 w-8 animate-spin text-primary/70" />
        </div>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="container mx-auto py-6">
        <div className="mb-6">
          <Link to="/alerts">
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Alerts
            </Button>
          </Link>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Alert not found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              The alert you're looking for doesn't exist or has been deleted.
            </p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Determine alert status
  let status = 'open';
  if (alert.resolved) status = 'resolved';
  else if (alert.acknowledged) status = 'acknowledged';

  // Get severity styles
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical":
        return <Badge variant="destructive" className="flex items-center gap-1"><AlertOctagon className="h-3 w-3" /> Critical</Badge>;
      case "warning":
        return <Badge className="bg-orange-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Warning</Badge>;
      case "info":
        return <Badge variant="outline" className="flex items-center gap-1"><Info className="h-3 w-3" /> Info</Badge>;
      default:
        return <Badge variant="outline" className="flex items-center gap-1"><Info className="h-3 w-3" /> {severity}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "resolved":
        return <Badge variant="outline" className="bg-green-100 text-green-800 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Resolved</Badge>;
      case "acknowledged":
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 flex items-center gap-1"><Clock className="h-3 w-3" /> Acknowledged</Badge>;
      case "open":
        return <Badge variant="outline" className="bg-red-100 text-red-800 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Open</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), "PPpp");
    } catch (e) {
      return "Invalid date";
    }
  };

  const getDeviceName = (deviceId: any) => {
    if (device) return device.name;
    if (!deviceId) return "Unknown Device";
    // Handle deviceId as string, ObjectId, or object with _id
    const deviceIdStr = typeof deviceId === 'object' 
      ? (deviceId._id?.toString() || deviceId.toString()) 
      : deviceId.toString();
    const foundDevice = devices.find((d: any) => {
      const dId = (d._id || d.id)?.toString();
      return dId === deviceIdStr;
    });
    return foundDevice ? foundDevice.name : `Device ${deviceIdStr}`;
  };

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex items-center gap-2"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <CardTitle className="text-2xl">{alert.details || "Alert Details"}</CardTitle>
                    {getSeverityBadge(alert.severity)}
                    {getStatusBadge(status)}
                  </div>
                  <CardDescription className="text-base mt-2">
                    {alert.message}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Alert Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Device</h4>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-medium">{getDeviceName(alert.deviceId)}</p>
                    <Link to={`/device/${normalizedDeviceId}`}>
                      <Button variant="ghost" size="sm" className="h-6 px-2">
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </Link>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Timestamp</h4>
                  <p className="text-base">{formatTimestamp(alert.timestamp)}</p>
                </div>
                {alert.threshold !== undefined && alert.threshold !== null && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Threshold</h4>
                    <p className="text-base">{alert.threshold}</p>
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Alert ID</h4>
                  <p className="text-sm font-mono">{alert._id || alert.id}</p>
                </div>
              </div>

              {/* Additional Details */}
              {alert.details && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Additional Details</h4>
                  <div className="bg-muted p-4 rounded-md">
                    <p className="text-sm whitespace-pre-wrap">{alert.details}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {status === "open" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => acknowledgeAlertMutation.mutate(alert._id || alert.id)}
                      disabled={acknowledgeAlertMutation.isPending}
                    >
                      {acknowledgeAlertMutation.isPending && (
                        <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Acknowledge
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => resolveAlertMutation.mutate(alert._id || alert.id)}
                      disabled={resolveAlertMutation.isPending}
                    >
                      {resolveAlertMutation.isPending && (
                        <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Resolve
                    </Button>
                  </>
                )}
                {status === "acknowledged" && (
                  <Button
                    variant="outline"
                    onClick={() => resolveAlertMutation.mutate(alert._id || alert.id)}
                    disabled={resolveAlertMutation.isPending}
                  >
                    {resolveAlertMutation.isPending && (
                      <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Resolve
                  </Button>
                )}
                {status === "resolved" && (
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteAlertMutation.mutate(alert._id || alert.id)}
                    disabled={deleteAlertMutation.isPending}
                  >
                    {deleteAlertMutation.isPending && (
                      <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Device Information */}
          {device && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Device Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Name</h4>
                  <p className="text-sm">{device.name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">IP Address</h4>
                  <p className="text-sm font-mono">{device.ipAddress || "N/A"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Type</h4>
                  <p className="text-sm">{device.type || "N/A"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Status</h4>
                  <Badge variant={device.status === "up" ? "default" : "destructive"}>
                    {device.status || "unknown"}
                  </Badge>
                </div>
                <Link to={`/device/${normalizedDeviceId}`}>
                  <Button variant="outline" size="sm" className="w-full">
                    View Device Details
                    <ExternalLink className="ml-2 h-3 w-3" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Alert Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Created</h4>
                <p className="text-sm">{formatTimestamp(alert.timestamp)}</p>
              </div>
              {alert.acknowledged && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Acknowledged</h4>
                  <p className="text-sm">Yes</p>
                </div>
              )}
              {alert.resolved && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Resolved</h4>
                  <p className="text-sm">Yes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

